type RouteHandler = () => void;

const routes = new Map<string, RouteHandler>();
let notFoundHandler: RouteHandler = () => {};
let currentCleanup: (() => void) | null = null;

export function onRoute(pattern: string, handler: RouteHandler) {
  routes.set(pattern, handler);
}

export function onNotFound(handler: RouteHandler) {
  notFoundHandler = handler;
}

export function navigate(path: string) {
  if (path === window.location.pathname + window.location.search) return;
  history.pushState(null, "", path);
  resolve();
}

export function replaceUrl(path: string) {
  if (path === window.location.pathname + window.location.search) return;
  history.replaceState(null, "", path);
  // Notify search param listeners without triggering full route change
  for (const listener of searchParamListeners) {
    listener();
  }
}

// Search param listeners — lightweight, don't trigger route re-render
const searchParamListeners = new Set<() => void>();

export function subscribeSearchParams(listener: () => void): () => void {
  searchParamListeners.add(listener);
  return () => searchParamListeners.delete(listener);
}

export function getSearchQuery(): string {
  return new URLSearchParams(window.location.search).get("q") ?? "";
}

export function resolve() {
  const pathname = window.location.pathname;

  // Notify React subscribers
  for (const listener of listeners) {
    listener();
  }
}

// React integration: subscribe to route changes
const listeners = new Set<() => void>();

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getPath(): string {
  return window.location.pathname;
}

export function getSearchParams(): URLSearchParams {
  return new URLSearchParams(window.location.search);
}

export function matchRoute(pattern: string, pathname: string): Record<string, string> | null {
  const patternParts = pattern.split("/");
  const pathParts = pathname.split("/");

  if (patternParts.length !== pathParts.length) return null;

  const params: Record<string, string> = {};

  for (let i = 0; i < patternParts.length; i++) {
    const pp = patternParts[i]!;
    const pathPart = pathParts[i]!;

    if (pp.startsWith(":")) {
      params[pp.slice(1)] = decodeURIComponent(pathPart);
    } else if (pp !== pathPart) {
      return null;
    }
  }

  return params;
}

// Initialize popstate listener
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => resolve());
}
